import {INDEPENDENT_DEALER, NOT_PROVIDED, US_STATES} from 'src/constants/DealershipInfoConstants';
import { FormErrors } from 'src/model/FormErrors';

export const createPhoneBlurHandler = (setErrors: Function, errorField: keyof FormErrors) => {
    return (e: React.FocusEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const digits = value.replace(/\D/g, '');
        if (!value || digits.length !== 10) {
            setErrors((prev: any) => ({ ...prev, [errorField]: true }));
        } else {
            setErrors((prev: any) => ({ ...prev, [errorField]: false }));
        }
    };
};

export const createStateBlurHandler = (setFormData: Function, setErrors: Function, errorField: keyof FormErrors) => {
    return (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target;
        const upperValue = e.target.value.toUpperCase();
        if (name.includes('.')) {
            const [parent, field] = name.split('.');
            setFormData((prev: any) => ({ ...prev, [parent]: { ...prev[parent], [field]: upperValue || '' } }));
            setErrors((prev: any) => ({ ...prev, [errorField]: !upperValue || !US_STATES.includes(upperValue) }));
        } else {
            setFormData((prev: any) => ({ ...prev, state: upperValue || '' }));
            setErrors((prev: any) => ({ ...prev, [errorField]: !upperValue || !US_STATES.includes(upperValue) }));
        }
    };
};

export const createZipCodeBlurHandler = (setErrors: Function, errorField: keyof FormErrors) => {
    return (e: React.FocusEvent<HTMLInputElement>) => {
        const zipValue = e.target.value;
        const isValid = /^\d{5}$/.test(zipValue);
        if (!zipValue || !isValid) {
            setErrors((prev: any) => ({ ...prev, [errorField]: true }));
        } else {
            setErrors((prev: any) => ({ ...prev, [errorField]: false }));
        }
    };
};

export const createEmailBlurHandler = (setErrors: Function, errorField: keyof FormErrors) => {
    return (e: React.FocusEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        if (!value || !isValid) {
            setErrors((prev: any) => ({ ...prev, [errorField]: true }));
        } else {
            setErrors((prev: any) => ({ ...prev, [errorField]: false }));
        }
    };
};

export const createSoftwareFieldBlurHandler = (setErrors: Function, fieldName: string, errorField: keyof FormErrors) => {
    return (formData: any) => {
        const value = formData.software[fieldName];
        setErrors((prev: any) => ({
            ...prev,
            [errorField]: value.trim() === ''
        }));
    };
};

export const createContactFieldBlurHandler = (setErrors: Function, fieldName: string, errorField: keyof FormErrors) => {
    return (formData: any) => {
        const value = formData.contact[fieldName];
        setErrors((prev: any) => ({
            ...prev,
            [errorField]: value.trim() === ''
        }));
    };
};

export const createBusinessFieldBlurHandler = (setErrors: Function, fieldName: string, errorField: keyof FormErrors) => {
    return (formData: any) => {
        const value = formData.business[fieldName];
        const isEmpty = fieldName === 'brands' 
            ? (Array.isArray(value) ? value.length === 0 : !value)
            : value.trim() === '';
        setErrors((prev: any) => ({
            ...prev,
            [errorField]: isEmpty
        }));
    };
};

export const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const validateRequiredFields = (formData: any, setErrors: Function) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneDigitsRegex = (phone: string) => phone.replace(/\D/g, '').length === 10;
    
    const errors = {
        firstNameError: formData.contact.firstName.trim() === '',
        lastNameError: formData.contact.lastName.trim() === '',
        titleError: formData.contact.title.trim() === '',
        emailError: formData.contact.email.trim() === '' || !emailRegex.test(formData.contact.email),
        phoneError: formData.contact.phone.trim() === '' || !phoneDigitsRegex(formData.contact.phone),
        streetAddressError: formData.business.streetAddress.trim() === '',
        cityError: formData.business.city.trim() === '',
        stateError: formData.business.state.trim() === '' || !US_STATES.includes(formData.business.state.toUpperCase()),
        zipCodeError: formData.business.zipCode.trim() === '' || !/^\d{5}$/.test(formData.business.zipCode),
        businessEmailError: formData.business.email.trim() === '' || !emailRegex.test(formData.business.email),
        businessNameError: formData.business.name.trim() === '' && formData.business.userInputName.trim() === '',
        businessPhoneError: formData.business.phone.trim() === '' || !phoneDigitsRegex(formData.business.phone),
        businessPrivacyPolicyError: false,
        businessWebsiteError: false,
        dealerLicenseError: formData.business.dealerLicenseNumber.trim() === '',
        oemDealerIdError: formData.business.oemDealerId.trim() === '',
        oemBrandError: false,
        vehicleConditionsError: formData.business.vehicleConditions.length === 0,
        leasingError: formData.business.leasing.trim() === '',
        parentCompanyError: formData.business.parentCompany.trim() === '',
        dmsProviderError: formData.software.dmsProvider.trim() === '',
        crmProviderError: formData.software.crmProvider.trim() === '',
        creditSubmissionProviderError: formData.software.creditSubmissionProvider.trim() === '',
        imsProviderError: formData.software.imsProvider.trim() === '',
        bdOwnerError: formData.bdOwner.trim() === '',
        userInputBusinessNameError: formData.business.name.trim() === NOT_PROVIDED && formData.business.userInputName.trim() === '',
        userInputBusinessGroupNameError: formData.business.groupName.trim() === NOT_PROVIDED && (formData.business.userInputGroupName.trim() === ''),
        fordWarrantyOnlyError: formData.business.brands?.length === 1 && formData.business.brands[0] === 'Ford' && formData.business.fordWarrantyOnly === undefined,
    };
    setErrors((prev: any) => ({ ...prev, ...errors }));
    return !Object.values(errors).some(error => error);
};

export const isFormValid = (formData: any, errors: any) => {
    return (
        formData.contact.firstName.trim() !== '' &&
        formData.contact.lastName.trim() !== '' &&
        formData.contact.title.trim() !== '' &&
        formData.contact.email.trim() !== '' &&
        formData.emailVerified &&
        formData.contact.phone.trim() !== '' &&
        !errors.phoneError &&
        formData.business.streetAddress.trim() !== '' &&
        formData.business.city.trim() !== '' &&
        formData.business.state.trim() !== '' &&
        !errors.stateError &&
        formData.business.zipCode.trim() !== '' &&
        !errors.zipCodeError &&
        formData.business.email.trim() !== '' &&
        !errors.businessEmailError &&
        ((formData.business.name.trim() !== '' && formData.business.name.trim() !== NOT_PROVIDED) || formData.business.userInputName.trim() !== '') &&
        (formData.business.groupName.trim() !== NOT_PROVIDED || (formData.business.userInputGroupName.trim() !== '' && formData.business.userInputGroupName.trim() !== INDEPENDENT_DEALER)) &&
        formData.business.phone.trim() !== '' &&
        !errors.businessPhoneError &&

        formData.business.dealerLicenseNumber.trim() !== '' &&
        formData.business.oemDealerId.trim() !== '' &&

        formData.business.vehicleConditions.length > 0 &&
        formData.business.leasing.trim() !== '' &&
        formData.business.parentCompany.trim() !== '' &&
        formData.software.dmsProvider.trim() !== '' &&
        formData.software.crmProvider.trim() !== '' &&
        formData.software.creditSubmissionProvider.trim() !== '' &&
        formData.software.imsProvider.trim() !== '' &&
        formData.bdOwner.trim() !== '' &&
        (!formData.business.brands || formData.business.brands.length !== 1 || formData.business.brands[0] !== 'Ford' || formData.business.fordWarrantyOnly !== undefined) &&
        formData.consentConfirmed
    );
};
